import CalendarToday from "@mui/icons-material/CalendarToday";
import {
  Box,
  Button,
  Container,
  Link,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  Modal,
  CircularProgress,
  Switch,
  FormControlLabel,
  useMediaQuery,
  Alert,
} from "@mui/material";
import React, { useState, useEffect } from "react";
import Footer from "../components/Footer";
import Navbar from "../components/Navbar";
import Frame from "../components/Frame";
import NotifyContainer from "./NotifyContainer";
import { getUserProfile, updateUserProfile, getTagInfo, PostNotifications } from "../services/api";
import Cookies from "js-cookie";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { convertFromISO } from "../utils/dateValidation";
import DatePickerField from "../components/DatePickerField";
import { useTranslation } from 'react-i18next';
import { validatePhoneNumberbyCode2 } from "../utils/validation";
import { useNotifications } from '../contexts/NotificationContext';


const Account = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [success, setSuccess] = useState(false);
  const [BackColor, setBackColor] = useState("");
  const [UpdateMessage, setUpdateMessage] = useState("");
  const { t, i18n } = useTranslation();
  const [isToggled, setIsToggled] = useState(false);
  const [phoneError, setPhoneError] = useState("");
  const [showNotifyContainer, setShowNotifyContainer] = useState(false);
  const [newNotification, setNewNotification] = useState(null);
  const { getAllNotifications, addNotification } = useNotifications();
  const [formData, setFormData] = useState({
    lastName: "",
    firstName: "",
    birthday: "",
    district: "", // Store English key directly
    phoneCode: "+852",
    phone: "",
    email: "",
    role: "",
  });
  const { id } = useParams();
  const isMobile = useMediaQuery("(max-width:850px)");

  //get tagId from parameter
  const tagId = searchParams.get('qr') || '';

  // District list - English keys only
  const districts = [
    "Central and Western District",
    "Wan Chai District",
    "Eastern District",
    "Southern District",
    "Yau Tsim Mong District",
    "Sham Shui Po District",
    "Kowloon City District",
    "Wong Tai Sin District",
    "Kwun Tong District",
    "Tsuen Wan District",
    "Tuen Mun District",
    "Yuen Long District",
    "North District",
    "Tai Po District",
    "Sha Tin District",
    "Sai Kung District",
    "Kwai Tsing District",
    "Islands District",
  ];

  // Get token from cookies
  const getUserId = () => {
    const id = Cookies.get("id");
    return id;
  };

  const handleToggleChange = (event) => {
    setIsToggled(event.target.checked);
  };

  // Fetch user profile data
  const fetchUserProfile = async () => {
    try {
      setLoading(true);
      const userId = getUserId();
      if (!userId) {
        setLoading(false);
        setError(true);
        setBackColor("#d32f2f");
        setUpdateMessage(t('ErrorAuthentication'));
        setTimeout(() => {
          setError(false);
        }, 2000);
        if (tagId) {
          navigate(`/login?qr=${tagId}`)
        }
        return;
      }

      const response = await getUserProfile(userId);
      const userData = response.data.user;

      // Parse phone number to extract country code and number
      const phoneNumber = userData.phoneNumber || "";
      let phoneCode = "+852";
      let phone = phoneNumber;

      // Check if phone number starts with a country code
      const countryCodeMatch = phoneNumber.match(/^(\+\d{2,3})/);
      if (countryCodeMatch) {
        phoneCode = countryCodeMatch[1];
        phone = phoneNumber.substring(phoneCode.length);
      }

      // Update form data with fetched user data
      setFormData({
        lastName: userData.lastName || "",
        firstName: userData.firstName || "",
        birthday: convertFromISO(userData.birthday),
        district: districts.includes(userData.district) ? userData.district : "", // Validate district
        phoneCode: phoneCode,
        phone: phone,
        email: userData.email || "",
        role: userData.role || "",
      });

      // Check if user has nearbyLostPets notification and district is set using context
      const allNotifications = getAllNotifications();
      const hasNearbyLostPetsNotification = allNotifications.some(
        notification => notification.type === 'nearbyLostPets'
      );
      
      if (hasNearbyLostPetsNotification && userData.district) {
        setIsToggled(true);
      }

      if (tagId) {
        switch (userData.role) {
          case "user":
            navigate(`/Geolocation?qr=${tagId}`);  //need to change here, it should check whether pet exists with this tagId
            break;
          case "admin":
            getTagInfo(tagId)
              .then((response) => {
                const isPTagAir = response.data.form.isPTagAir;
                if (isPTagAir) {
                  navigate(`/landing`);
                }
              })
              .catch((error) => {
                console.error("Error fetching Tag Info:", error);
              });
            navigate(`/qr_verification?qr=${tagId}`)
          default:
            return
        }
      }
    } catch (err) {
      console.error("Error fetching user profile:", err);
      setLoading(false);
      setError(true);
      setBackColor("#d32f2f");
      setUpdateMessage(t('ErrorFailingLoadProfile'));
      setTimeout(() => {
        setError(false);
      }, 2000);
    } finally {
      setLoading(false);
    }
  };

  // Update user profile
  const handleUpdateProfile = async () => {
    try {
      setLoading(true);

      const userId = getUserId();
      if (!userId) {
        setLoading(false);
        setError(true);
        setBackColor("#d32f2f");
        setUpdateMessage(t('ErrorAuthentication'));
        setTimeout(() => {
          setError(false);
        }, 2000);
        return;
      }

      // Validate phone number
      const phoneValidationError = validatePhoneNumberbyCode2(
        { phoneNumber2: formData.phone, phoneCode: formData.phoneCode },
        t
      );

      if (phoneValidationError) {
        setLoading(false);
        setPhoneError(phoneValidationError);
        return;
      }

      setPhoneError("");

      // Convert DD/MM/YYYY to ISO string format for backend
      const convertDateForBackend = (dateString) => {
        if (!dateString) return "";
        const parts = dateString.split("/");
        if (parts.length === 3) {
          const day = parts[0];
          const month = parts[1];
          const year = parts[2];
          // Return ISO string format (YYYY-MM-DD)
          const date = new Date(year, month - 1, day);
          return date.toISOString();
        }
        return dateString;
      };

      const updateData = {
        userId: userId,
        firstName: formData.firstName,
        lastName: formData.lastName,
        birthday: convertDateForBackend(formData.birthday),
        email: formData.email,
        district: formData.district, // Already English key
        phoneNumber: formData.phoneCode + formData.phone,
      };

      if (isToggled && formData.district) {
        const nearbyLostPets = `https://www.ptag.com.hk/all_lost_pets?region=${formData.district}`;
        const notificationData = {
          userId: userId,
          type: "nearbyLostPets",
          nearbyPetLost: nearbyLostPets,
        }

        const response = await PostNotifications(notificationData);

        if (response.status === 200) {
          // Add to context and show the newly created notification
          if (response.data && response.data.notification) {
            addNotification(response.data.notification);
            setNewNotification(response.data.notification);
            setShowNotifyContainer(true);
            
            // Hide after 3 seconds
            setTimeout(() => {
              setShowNotifyContainer(false);
              setNewNotification(null);
            }, 2000);
          }

          setSuccess(true);
          setBackColor("#4CAF50");
          setUpdateMessage(t('SuccessProfileUpdated'));
          setTimeout(() => {
            setSuccess(false);
          }, 2000);


        } else {
          setLoading(false);
          setError(true);
          setBackColor("#d32f2f");
          setUpdateMessage(t('ErrorUpdatingProfile'));
          setTimeout(() => {
            setError(false);
          }, 2000);
          return;
        }
      }

      const response = await updateUserProfile(updateData);

      if (response.status === 200) {
        setSuccess(true);
        setBackColor("#4CAF50");
        setUpdateMessage(t('SuccessProfileUpdated'));
        setTimeout(() => {
          setSuccess(false);
        }, 2000);
      } else {
        setLoading(false);
        setError(true);
        setBackColor("#d32f2f");
        setUpdateMessage(t('ErrorUpdatingProfile'));
        setTimeout(() => {
          setError(false);
        }, 2000);
      }
    } catch (err) {
      console.error("Error updating profile:", err);
      if (err.response.data.code === "PHONE_EXISTS") {
        setLoading(false);
        setError(true);
        setBackColor("#d32f2f");
        setUpdateMessage(t('ExistedUser2'));
        setTimeout(() => {
          setError(false);
        }, 2000);
      }
      else if (err.response.data.code === "EMAIL_EXISTS") {
        setLoading(false);
        setError(true);
        setBackColor("#d32f2f");
        setUpdateMessage(t('ExistedUser'));
        setTimeout(() => {
          setError(false);
        }, 2000);
      }
      else {
        setLoading(false);
        setError(true);
        setBackColor("#d32f2f");
        setUpdateMessage(t('ErrorUpdatingProfile'));
        setTimeout(() => {
          setError(false);
        }, 2000);
      }

    } finally {
      setLoading(false);
    }
  };

  // Handle form field changes
  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleOpenPasswordModal = (e) => {
    e.preventDefault();
    setShowPasswordModal(true);
  };

  const handleClosePasswordModal = () => {
    setShowPasswordModal(false);
  };

  // Fetch user profile on component mount
  useEffect(() => {
    fetchUserProfile();
  }, [id]);

  if (loading && !formData.email) {
    return (
      <Box
        sx={{
          bgcolor: "#fcf8f3",
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Typography variant="h6" color="text.secondary">
            {/* 載入中... */}
            <img src="https://petpetclub.s3.ap-southeast-1.amazonaws.com/user-uploads/pets/68cb893a0feda12f2dbcc02b/68d4c8ce22baedfbe8b852dd.gif" alt=""
              style={{
                width: '150px',
                height: 'auto',
                mixBlendMode: 'multiply',
              }} />
            <div
              style={{
                fontWeight: "500px",
                fontSize: "15px",
                textAlign: "center",
                fontFamily: "Lucida Console",

              }}>
              {t('Loading')}
            </div>
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: "#fcf8f3", minHeight: "100vh" }}>
      {/* Header */}
      <Navbar />

      {/* Main Content */}
      <Container
        maxWidth="lg"
        sx={{
          py: { xs: 4, md: 8 },
          px: { xs: 2, md: 3 },
          mt: "70px", // Add top margin to account for fixed navbar height
        }}
      >
        <Typography
          variant="h4"
          sx={{
            mb: { xs: 3, md: 5 },
            fontWeight: "bold",
            fontSize: { xs: "24px", sm: "28px", md: "32px" },
          }}
        >
          {t('PersonalProfile')}
        </Typography>

        <Stack
          spacing={{ xs: 4, md: 6 }}
          sx={{ maxWidth: { xs: "100%", sm: 400, md: 450 } }}
        >
          <Stack spacing={{ xs: 2.5, md: 3 }}>
            {/* Name */}
            <Box>
              <Typography
                variant="h6"
                sx={{
                  mb: { xs: 0.5, md: 1 },
                  fontSize: { xs: "16px", md: "18px" },
                  fontWeight: "600",
                }}
              >
                {t('LastName')}
              </Typography>
              <TextField
                fullWidth
                variant="outlined"
                value={formData.lastName}
                onChange={(e) => handleInputChange("lastName", e.target.value)}
                InputProps={{
                  sx: {
                    bgcolor: "white",
                    borderRadius: "3px",
                    height: { xs: "45px", md: "56px" },
                    fontSize: { xs: "14px", md: "16px" },
                  },
                }}
              />
            </Box>

            {/* Name */}
            <Box>
              <Typography
                variant="h6"
                sx={{
                  mb: { xs: 0.5, md: 1 },
                  fontSize: { xs: "16px", md: "18px" },
                  fontWeight: "600",
                }}
              >
                {t('FirstName')}
              </Typography>
              <TextField
                fullWidth
                variant="outlined"
                value={formData.firstName}
                onChange={(e) => handleInputChange("firstName", e.target.value)}
                InputProps={{
                  sx: {
                    bgcolor: "white",
                    borderRadius: "3px",
                    height: { xs: "45px", md: "56px" },
                    fontSize: { xs: "14px", md: "16px" },
                  },
                }}
              />
            </Box>

            {/* Date of Birth */}
            <Box>
              <Typography
                variant="h6"
                sx={{
                  mb: { xs: 0.5, md: 1 },
                  fontSize: { xs: "16px", md: "18px" },
                  fontWeight: "600",
                }}
              >
                {t('DateofBirth')}
              </Typography>
              {/* <TextField
                fullWidth
                placeholder="DD/MM/YYYY"
                variant="outlined"
                value={formData.birthday}
                onChange={(e) => {
                  const value = e.target.value
                    .replace(/[^0-9/]/g, "")
                    .slice(0, 10);
                  handleInputChange("birthday", value);
                }}
                InputProps={{
                  sx: {
                    bgcolor: "white",
                    borderRadius: "3px",
                    height: { xs: "45px", md: "56px" },
                    fontSize: { xs: "14px", md: "16px" },
                  },
                  endAdornment: (
                    <CalendarToday
                      sx={{ fontSize: { xs: "20px", md: "24px" } }}
                    />
                  ),
                }}
              /> */}
              <DatePickerField
                id="birthday"
                name="birthday"
                placeholder="DD/MM/YYYY"
                variant="outlined"
                value={formData.birthday || ""}
                // onChange={(e) => {
                //   const value = e.target.value
                //     .replace(/[^0-9/]/g, "")
                //     .slice(0, 10);
                //   handleInputChange("birthday", value);
                // }}
                onChange={(value) =>
                  handleInputChange("birthday", value)
                }
                required
              />
            </Box>

            {/* District */}
            <Box>
              <Typography
                variant="h6"
                sx={{
                  mb: { xs: 0.5, md: 1 },
                  fontSize: { xs: "16px", md: "18px" },
                  fontWeight: "600",
                  display: "inline",
                }}
              >
                {t('Area')}
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  mb: { xs: 0.5, md: 1 },
                  paddingLeft: "10px",
                  fontSize: { xs: "12px", md: "14px" },
                  fontWeight: "400",
                  display: "inline",
                  color: "#969696",
                }}
              >
                {t('LostPetNotifications')}
              </Typography>
              <FormControlLabel
                label=""
                control={
                  <Switch
                    sx={{
                      "& .MuiSwitch-switchBase": {
                        color: "white", // Thumb color when unchecked
                        "&.Mui-checked": {
                          color: "#FFB60C", // Thumb color when checked
                        },
                      },
                      "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                        backgroundColor: "#FFB60C", // Ensure track color when checked
                      },
                      transform: isMobile ? "scale(0.8)" : "scale(1)"
                    }}
                    checked={isToggled}
                    onChange={handleToggleChange}
                    color="primary"
                  />
                }
                sx={{ m: 0 }} // Remove default margin
              />
              <Select
                fullWidth
                displayEmpty
                value={formData.district}
                onChange={(e) => handleInputChange("district", e.target.value)}
                renderValue={(value) => value ? t(value) : t('PleaseSelectArea')}
                sx={{
                  bgcolor: "white",
                  borderRadius: "3px",
                  height: { xs: "45px", md: "56px" },
                  fontSize: { xs: "14px", md: "16px" },
                }}
              >
                <MenuItem value="">
                  <em>{t('PleaseSelectArea')}</em>
                </MenuItem>
                {districts.map((district) => (
                  <MenuItem key={district} value={district}>
                    {t(district)}
                  </MenuItem>
                ))}
              </Select>
            </Box>

            {/* Phone Number */}
            <Box>
              <Typography
                variant="h6"
                sx={{
                  mb: { xs: 0.5, md: 1 },
                  fontSize: { xs: "16px", md: "18px" },
                  fontWeight: "600",
                }}
              >
                {t('phoneNumber')}
              </Typography>
              <Box sx={{ display: 'flex', gap: '10px' }}>
                <Select
                  value={formData.phoneCode}
                  onChange={(e) => handleInputChange("phoneCode", e.target.value)}
                  sx={{
                    bgcolor: "white",
                    borderRadius: "3px",
                    height: { xs: "45px", md: "56px" },
                    fontSize: { xs: "14px", md: "16px" },
                    width: { xs: "90px", md: "110px" },
                  }}
                >
                  <MenuItem value="+852">+852</MenuItem>
                  <MenuItem value="+853">+853</MenuItem>
                  <MenuItem value="+86">+86</MenuItem>
                  <MenuItem value="+865">+865</MenuItem>
                  <MenuItem value="+81">+81</MenuItem>
                  <MenuItem value="+880">+880</MenuItem>
                </Select>
                <TextField
                  fullWidth
                  type="tel"
                  placeholder={t('phoneNumber')}
                  variant="outlined"
                  value={formData.phone}
                  onChange={(e) => {
                    handleInputChange("phone", e.target.value);
                    setPhoneError("");
                  }}
                  InputProps={{
                    sx: {
                      bgcolor: "white",
                      borderRadius: "3px",
                      height: { xs: "45px", md: "56px" },
                      fontSize: { xs: "14px", md: "16px" },
                    },
                  }}
                />
              </Box>
              {phoneError && (
                <Typography
                  sx={{
                    color: "#f10000",
                    fontSize: { xs: "12px", md: "14px" },
                    mt: 0.5,
                  }}
                >
                  {phoneError}
                </Typography>
              )}
            </Box>

            {/* Email */}
            <Box>
              <Typography
                variant="h6"
                sx={{
                  mb: { xs: 0.5, md: 1 },
                  fontSize: { xs: "16px", md: "18px" },
                  fontWeight: "600",
                }}
              >
                {t('email')}
              </Typography>
              <TextField
                fullWidth
                variant="outlined"
                value={formData.email}
                onChange={(e) => handleInputChange("email", e.target.value)}
                InputProps={{
                  sx: {
                    bgcolor: "white",
                    borderRadius: "3px",
                    height: { xs: "45px", md: "56px" },
                    fontSize: { xs: "14px", md: "16px" },
                  },
                }}
              />
            </Box>
          </Stack>

          <Box
            sx={{
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              justifyContent: { xs: "center", md: "flex-start" },
              mt: { xs: 5, md: 10 },
              mx: 3,
              gap: 2,
            }}
          >
            <Button
              variant="contained"
              onClick={handleUpdateProfile}
              disabled={loading}
              sx={{
                bgcolor: "#ffb60c",
                color: "#050505",
                width: { xs: "100%", sm: 133 },
                height: { xs: "45px", md: "auto" },
                borderRadius: "3px",
                fontSize: { xs: "14px", md: "16px" },
                fontWeight: "600",
                "&:hover": {
                  bgcolor: "#e6a40b",
                },
                "&:disabled": { background: "rgba(255, 182, 12, 0.5)" },
                cursor: loading ? "not-allowed" : "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "10px",

              }}
            >
              {t('Save')}
              {loading && (
                <span
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                  }}
                >
                  <CircularProgress size={20} color="inherit" />
                </span>
              )}
            </Button>
            <Button
              type="button"
              variant="outlined"
              color="text.secondary"
              onClick={() => navigate("/pet-management")}
              sx={{
                minWidth: 120,
                "&:hover": {
                  backgroundColor: "black",
                  color: "white",
                },
              }}
            >
              {t('cancel')}
            </Button>
          </Box>
        </Stack>
      </Container>

      <Footer />

      {/* Password Change Modal */}
      <Modal
        open={showPasswordModal}
        onClose={handleClosePasswordModal}
        aria-labelledby="password-change-modal"
        aria-describedby="password-change-form"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            outline: "none",
            width: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            p: { xs: 2, md: 0 },
          }}
        >
          <Frame onClose={handleClosePasswordModal} />
        </Box>
      </Modal>
      <Box
        sx={{
          position: "fixed",
          bottom: 20,
          right: (success || error) ? 20 : -500,
          backgroundColor: BackColor,
          color: "#fff",
          padding: isMobile ? "12px 18px" : "16px 24px",
          borderRadius: 2,
          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
          zIndex: 9999,
          minWidth: 250,
          display: "flex",
          alignItems: "center",
          gap: 2,
          transition: (success || error)
            ? "right 0.4s ease-out"
            : "right 0.4s ease-in",
        }}
      >
        <Box
          sx={{
            width: 24,
            height: 24,
            borderRadius: "50%",
            backgroundColor: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          {error ? (
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M18 6L6 18M6 6L18 18"
                stroke={BackColor}
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          ) : (
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M20 6L9 17L4 12"
                stroke={BackColor}
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          )}
        </Box>
        <Typography fontWeight={600} fontSize={isMobile ? 12 : 16}>
          {UpdateMessage}
        </Typography>
      </Box>
      
      {/* Notification Container */}
      <NotifyContainer 
        open={showNotifyContainer} 
        onClose={() => {
          setShowNotifyContainer(false);
          setNewNotification(null);
        }}
        singleNotification={newNotification}
      />
    </Box>
  );
};

export default Account;
